// © 2026 AttainIP Engineering
import type { EnterpriseSummary } from '@/api/queries';

/**
 * Which organisations a guest may BELONG to, and how they are named
 * (RFC-0510 W44).
 *
 * Pure, and in its own module rather than beside the panel that uses them: the
 * rules below are the ones worth testing on their own, and a leaf module with
 * no React import can be imported by a test without mounting anything.
 */

/** The Attain organisation. Virtually every guest this surface grants is an
 *  Attain employee, so it is pre-selected when the environment has it —
 *  and nothing is pre-selected when it does not, rather than a guess. */
export const ATTAIN_DOMAIN = 'attainip.ai';

/** A firm's label wherever one is shown: the name an operator recognises, with
 *  the domain that disambiguates two firms sharing one. */
export function orgLabel(org: EnterpriseSummary): string {
  return `${org.name} · ${org.domain}`;
}

/**
 * The organisations offered as a guest's home.
 *
 * Two exclusions, both load-bearing rather than tidy:
 *  - the HOST firm, because "guest" means belongs elsewhere. Identity refuses
 *    it (`home_org_is_host_org`), and this keeps the operator from discovering
 *    that by being refused.
 *  - unactivated firms, which have no org id at all — there is nothing to put
 *    on the wire for them.
 */
export function homeOrgOptions(
  enterprises: EnterpriseSummary[],
  hostOrgId: string,
): EnterpriseSummary[] {
  return enterprises
    .filter((org) => org.activated && !!org.org_id && org.org_id !== hostOrgId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The pre-selected home organisation: Attain, when this environment has it. */
export function defaultHomeOrgId(options: EnterpriseSummary[]): string {
  const attain = options.find((org) => org.domain === ATTAIN_DOMAIN);
  return attain?.org_id ?? '';
}
